import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function LandlordSafetyCertificatesPage() {
  useEffect(() => {
    document.title = 'Landlord Safety Certificates Watford | Beyond Electrical Services';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Landlord electrical safety certificates in Watford. EICR testing, compliance certificates & legal requirements met. NICEIC approved. Fast turnaround. Call +44 7989 675952.');
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).rel = 'canonical';
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).href = `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/safety-compliance/landlord-safety-certificates`;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'landlord-schema';
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Beyond Electrical Services",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/safety-compliance/landlord-safety-certificates`,
        "telephone": "+447989675952",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Watford",
          "addressRegion": "Hertfordshire",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 51.6565,
          "longitude": -0.3957
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "priceRange": "££"
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Landlord Safety Certificates Watford",
        "description": "Complete landlord electrical safety certification service in Watford. EICR testing, smoke alarm checks, and all required documentation for rental property compliance.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Beyond Electrical Services",
          "telephone": "+447989675952"
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "serviceType": "Landlord Safety Certificates",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/safety-compliance/landlord-safety-certificates`
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Do landlords in Watford need an electrical safety certificate?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. Under the Electrical Safety Standards in the Private Rented Sector (England) Regulations 2020, all landlords in Watford must have a valid EICR carried out every 5 years and provide a copy to tenants."
            }
          },
          {
            "@type": "Question",
            "name": "What is the fine for not having a landlord electrical certificate in Watford?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Landlords in Watford who fail to comply with electrical safety regulations can face fines of up to £30,000 from the local authority."
            }
          }
        ]
      }
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('landlord-schema');
      if (s) document.head.removeChild(s);
    };
  }, []);

  const areasServed = [
    'Watford Town Centre',
    'Oxhey',
    'Bushey',
    'Croxley Green',
    'Abbots Langley',
    'Rickmansworth',
    'Garston',
    'North Watford',
    'West Watford',
    'Carpenders Park',
    'Leavesden',
    'Nascot Wood',
    'Holywell',
    'Meriden',
    'Cassiobury'
  ];

  const whatsIncluded = [
    {
      icon: '📋',
      title: 'EICR Certificate',
      description: 'Mandatory electrical safety inspection every 5 years or at change of tenancy'
    },
    {
      icon: '🔥',
      title: 'Smoke Alarm Testing',
      description: 'Verification of working smoke alarms on every floor as required by law'
    },
    {
      icon: '💨',
      title: 'Carbon Monoxide Check',
      description: 'CO alarm testing in rooms with solid fuel appliances'
    },
    {
      icon: '⚡',
      title: 'Safety Compliance',
      description: 'Full compliance with Electrical Safety Standards in the Private Rented Sector Regulations 2020'
    },
    {
      icon: '📄',
      title: 'Tenancy Documentation',
      description: 'All certificates provided in digital and physical format for tenant records'
    },
    {
      icon: '🛡️',
      title: 'Legal Protection',
      description: 'Protect yourself from fines up to £30,000 with proper certification'
    }
  ];

  const benefits = [
    {
      icon: '⚖️',
      title: 'Legal Compliance',
      description: 'Meet all landlord obligations under current UK rental property legislation'
    },
    {
      icon: '🏠',
      title: 'Tenant Safety',
      description: 'Ensure your tenants live in a safe, electrically sound property'
    },
    {
      icon: '💼',
      title: 'Insurance Valid',
      description: 'Maintain valid landlord insurance with up-to-date safety certificates'
    },
    {
      icon: '📱',
      title: 'Fast Service',
      description: 'Quick turnaround to meet tenancy deadlines and legal requirements'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628]"></div>
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://public.readdy.ai/ai/img_res/c5ec57781ea86cc6135b79638291ce3b.jpg" 
            alt="Landlord Safety Certificates Background" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">🏆 NICEIC Approved</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">📍 Watford</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Landlord Safety Certificates<br />
            <span className="text-[#D4AF37]">in Watford</span>
          </h1>
          
          <p className="text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed">
            Complete landlord electrical safety certification service. EICR testing, smoke alarm checks, and all required documentation for rental property compliance.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Complete Landlord Certification Package</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to meet legal requirements for rental properties
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whatsIncluded.map((item, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-lg mb-6 text-3xl">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Why Landlord Certificates Matter</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect your investment and meet your legal obligations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl hover:bg-gray-50 transition-all duration-300"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-full mb-6 text-4xl mx-auto shadow-lg">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Beyond Electrical?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Watford's trusted partner for landlord electrical safety compliance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-shield-check-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">NICEIC Approved</h3>
              <p className="text-gray-300 text-sm">Official certification accepted everywhere</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-time-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Fast Turnaround</h3>
              <p className="text-gray-300 text-sm">Meet tenancy deadlines with ease</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-file-list-3-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Complete Package</h3>
              <p className="text-gray-300 text-sm">All certificates in one visit</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-customer-service-2-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Landlord Experts</h3>
              <p className="text-gray-300 text-sm">Specialists in rental property compliance</p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 flex items-center justify-center bg-[#D4AF37] rounded-full text-4xl flex-shrink-0">
                🏆
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">Landlord Compliance Specialists</h3>
                <p className="text-gray-300 leading-relaxed">
                  We understand landlord requirements inside out. Our NICEIC approved electricians provide all necessary certificates with clear documentation for your records and tenant handover.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Areas We Cover</h2>
            <p className="text-xl text-gray-600">
              Landlord safety certificates across Watford and surrounding areas
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {areasServed.map((area, index) => (
              <span 
                key={index}
                className="px-6 py-3 bg-white rounded-full text-[#0A1628] font-medium border border-gray-200 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need Landlord Certificates?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Get all your rental property safety certificates from NICEIC approved electricians. Fast service to meet tenancy deadlines in Watford.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Request Callback
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-300">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Same-Day Service</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>NICEIC Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Complete Package</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}